import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const sections = [
  {
    title: "Eligibility & Verification",
    body: [
      "To be listed as a service partner, you must complete our verification process — identity documents and relevant skill/experience details.",
      "ClickNido may decline or remove a partner listing at its discretion if verification cannot be completed or standards aren't met.",
    ],
  },
  {
    title: "Independent Contractor Status",
    body: [
      "Service partners on ClickNido are independent professionals, not employees of ClickNido. You choose which bookings to accept and when to be available.",
      "You're responsible for your own applicable taxes and compliance as an independent service provider.",
    ],
  },
  {
    title: "Partner Responsibilities",
    body: [
      "Arrive for accepted bookings on time, and communicate promptly if you're delayed or need to cancel.",
      "Carry out work to a professional standard, using appropriate care with the customer's property.",
      "Do not solicit customers to book or pay you directly outside the platform for work discovered through ClickNido.",
    ],
  },
  {
    title: "Payments & Payouts",
    body: [
      "Earnings for completed bookings are calculated per the pricing shown at the time of booking, minus the applicable platform fee.",
      "Payouts are processed to your registered payment details on the cycle communicated in the Partner App.",
    ],
  },
  {
    title: "Conduct Standards",
    body: [
      "Repeated declines, no-shows, poor ratings, or customer complaints may trigger a review of your partner account.",
      "Serious violations — safety issues, harassment, fraud — can result in immediate suspension.",
    ],
  },
  {
    title: "Termination",
    body: [
      "You may deactivate your partner account at any time.",
      "ClickNido may suspend or terminate a partner account for violating these terms, repeated performance issues, or failed re-verification.",
    ],
  },
];

const ServicePartnerTerms = () => {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.breadcrumb}>Home &nbsp;›&nbsp; Service Partner Terms &amp; Conditions</div>

        <h1 style={styles.title}>Service Partner Terms &amp; Conditions</h1>
        <p style={styles.intro}>
          These terms apply specifically to professionals who register on ClickNido
          to offer services through the platform.
        </p>

        {sections.map((sec) => (
          <div key={sec.title} style={styles.card}>
            <h2 style={styles.cardTitle}>{sec.title}</h2>
            <ul style={styles.list}>
              {sec.body.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        ))}

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Contact Us</h2>
          <p style={styles.contactText}>Questions as a service partner? Reach out to us anytime.</p>
          <button style={styles.inlineLink} onClick={() => navigate("/contact-us")}>
            Contact Us <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "#f5f6fb",
    minHeight: "100vh",
    padding: "40px 16px",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#111827",
  },
  container: { maxWidth: "860px", margin: "0 auto" },
  breadcrumb: { color: "#7b8198", fontSize: "13px", fontWeight: 600, marginBottom: "14px" },
  title: { margin: 0, fontSize: "32px", fontWeight: 800, color: "#1c2148", marginBottom: "12px" },
  intro: { margin: "0 0 28px", fontSize: "15px", lineHeight: 1.75, color: "#5c6480", maxWidth: "700px" },
  card: {
    background: "#fff",
    borderRadius: "20px",
    padding: "26px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
    marginBottom: "18px",
  },
  cardTitle: { margin: "0 0 14px", fontSize: "17px", fontWeight: 800, color: "#21284d" },
  list: { margin: 0, paddingLeft: "20px", color: "#5c6480", fontSize: "14px", lineHeight: 1.9 },
  contactText: { margin: "0 0 14px", fontSize: "14px", lineHeight: 1.8, color: "#5c6480" },
  inlineLink: {
    background: "transparent",
    border: "none",
    color: "#6d5efc",
    fontWeight: 700,
    fontSize: "14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    padding: 0,
  },
};

export default ServicePartnerTerms;
